import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { tokenManager } from '../../services/api';
import axios from 'axios';
import './AdminOpinionPage.css';

const OPINION_API_URL = process.env.REACT_APP_OPINION_API_URL || 'http://localhost:8002';

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'pending', label: '待处理' },
  { value: 'processing', label: '处理中' },
  { value: 'resolved', label: '已解决' },
  { value: 'closed', label: '已关闭' },
];

const STATUS_COLORS = {
  pending: '#f59e0b',
  processing: '#3b82f6',
  resolved: '#10b981',
  closed: '#6b7280',
};

function AdminOpinionPage() {
  const { isAuthenticated, user, isAdmin, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [opinions, setOpinions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedOpinion, setSelectedOpinion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateNotes, setUpdateNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const loadOpinions = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError('');
    
    try {
      const accessToken = tokenManager.getAccessToken();
      const params = {};
      if (selectedStatus) {
        params.status = selectedStatus;
      }
      
      const response = await axios.get(`${OPINION_API_URL}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        params,
      });
      
      setOpinions(response.data);
      
      // 预加载图片为 blob URL
      const newImageUrls = {};
      
      for (const opinion of response.data) {
        if (opinion.image_paths && opinion.image_paths.length > 0) {
          for (let index = 0; index < opinion.image_paths.length; index++) {
            const imageKey = `${opinion.id}-${index}`;
            const imageUrl = `${OPINION_API_URL}/${opinion.id}/images/${index}`;
            
            try {
              const imageResponse = await axios.get(imageUrl, {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                },
                responseType: 'blob',
              });
              const blobUrl = URL.createObjectURL(imageResponse.data);
              newImageUrls[imageKey] = blobUrl;
            } catch (err) {
              // 图片加载失败，使用占位符
              newImageUrls[imageKey] = null;
            }
          }
        }
      }
      
      setImageUrls(newImageUrls);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 
        err.response?.data?.message || 
        err.message || 
        '加载意见列表失败';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedStatus]);

  // 检查权限（ProtectedRoute 已经处理了权限检查，这里作为额外保护）
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login', { replace: true });
      return;
    }
    
    if (!isAdmin) {
      setError('您没有权限访问此页面');
      setTimeout(() => {
        // 与 ProtectedRoute 保持一致，跳转到 dashboard 而不是首页
        navigate('/dashboard', { replace: true });
      }, 2000);
      return;
    }
    
    loadOpinions();
  }, [isAuthenticated, user, isAdmin, navigate, loadOpinions]);

  // 当筛选状态改变时，重新加载数据
  useEffect(() => {
    if (isAuthenticated && user && isAdmin) {
      loadOpinions();
    }
  }, [selectedStatus, isAuthenticated, user, isAdmin, loadOpinions]);

  const handleOpenModal = (opinion) => {
    setSelectedOpinion(opinion);
    setUpdateStatus(opinion.status);
    setUpdateNotes(opinion.notes || '');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOpinion(null);
    setUpdateStatus('');
    setUpdateNotes('');
  };

  const handleUpdateStatus = async () => {
    if (!selectedOpinion) return;
    
    setIsUpdating(true);
    setError('');
    
    try {
      const accessToken = tokenManager.getAccessToken();
      const response = await axios.patch(
        `${OPINION_API_URL}/${selectedOpinion.id}`,
        {
          status: updateStatus,
          notes: updateNotes || null,
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      // 更新列表中的意见
      setOpinions(prev => prev.map(op => 
        op.id === selectedOpinion.id ? response.data : op
      ));
      
      handleCloseModal();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 
        err.response?.data?.message || 
        err.message || 
        '更新失败';
      setError(errorMsg);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusLabel = (status) => {
    const option = STATUS_OPTIONS.find(opt => opt.value === status);
    return option ? option.label : status;
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="admin-opinion-page">
        <div className="error-container">
          <div className="error-message">您没有权限访问此页面</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-opinion-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1>意见管理</h1>
          <p>查看和处理用户提交的意见反馈</p>
        </div>

        {error && (
          <div className="error-alert">
            ⚠️ {error}
          </div>
        )}

        <div className="filter-section">
          <label htmlFor="status-filter">状态筛选：</label>
          <select
            id="status-filter"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="status-filter"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="refresh-button"
            onClick={loadOpinions}
            disabled={loading}
          >
            🔄 刷新
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>加载中...</p>
          </div>
        ) : opinions.length === 0 ? (
          <div className="empty-container">
            <p>暂无意见</p>
          </div>
        ) : (
          <div className="opinions-list">
            {opinions.map(opinion => (
              <div key={opinion.id} className="opinion-card">
                <div className="opinion-header-row">
                  <div className="opinion-id">#{opinion.id}</div>
                  <div 
                    className="status-badge"
                    style={{ backgroundColor: STATUS_COLORS[opinion.status] || '#6b7280' }}
                  >
                    {getStatusLabel(opinion.status)}
                  </div>
                </div>
                
                <div className="opinion-user-info">
                  <span className="user-name">{opinion.username}</span>
                  <span className="user-email">{opinion.email}</span>
                </div>
                
                <div className="opinion-content">
                  {opinion.content}
                </div>
                
                {opinion.image_paths && opinion.image_paths.length > 0 && (
                  <div className="opinion-images">
                    {opinion.image_paths.map((path, index) => {
                      const imageKey = `${opinion.id}-${index}`;
                      const imageUrl = imageUrls[imageKey];
                      
                      if (!imageUrl) {
                        return (
                          <div key={index} className="image-placeholder">
                            图片加载失败
                          </div>
                        );
                      }
                      
                      return (
                        <img
                          key={index}
                          src={imageUrl}
                          alt={`图片 ${index + 1}`}
                          className="opinion-image"
                          onClick={() => setPreviewImageUrl(imageUrl)}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      );
                    })}
                  </div>
                )}
                
                <div className="opinion-meta">
                  <div className="meta-item">
                    <span className="meta-label">提交时间：</span>
                    <span>{formatDate(opinion.created_at)}</span>
                  </div>
                  {opinion.handled_by && (
                    <div className="meta-item">
                      <span className="meta-label">处理人：</span>
                      <span>{opinion.handled_by}</span>
                    </div>
                  )}
                  {opinion.handled_at && (
                    <div className="meta-item">
                      <span className="meta-label">处理时间：</span>
                      <span>{formatDate(opinion.handled_at)}</span>
                    </div>
                  )}
                </div>
                
                {opinion.notes && (
                  <div className="opinion-notes">
                    <strong>备注：</strong>
                    <p>{opinion.notes}</p>
                  </div>
                )}
                
                <button
                  type="button"
                  className="handle-button"
                  onClick={() => handleOpenModal(opinion)}
                >
                  处理
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 处理意见模态框 */}
      {showModal && selectedOpinion && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>处理意见 #{selectedOpinion.id}</h2>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="modal-section">
                <label>用户信息</label>
                <p>{selectedOpinion.username} ({selectedOpinion.email})</p>
              </div>
              
              <div className="modal-section">
                <label>意见内容</label>
                <div className="content-display">{selectedOpinion.content}</div>
              </div>
              
              <div className="modal-section">
                <label htmlFor="update-status">状态</label>
                <select
                  id="update-status"
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                  className="status-select"
                >
                  {STATUS_OPTIONS.filter(opt => opt.value).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="modal-section">
                <label htmlFor="update-notes">备注</label>
                <textarea
                  id="update-notes"
                  value={updateNotes}
                  onChange={(e) => setUpdateNotes(e.target.value)}
                  placeholder="输入处理备注..."
                  rows={4}
                  className="notes-textarea"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCloseModal}
              >
                取消
              </button>
              <button
                type="button"
                className="save-button"
                onClick={handleUpdateStatus}
                disabled={isUpdating}
              >
                {isUpdating ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 图片预览模态框 */}
      {previewImageUrl && (
        <div className="image-preview-overlay" onClick={() => setPreviewImageUrl(null)}>
          <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="image-preview-close" 
              onClick={() => setPreviewImageUrl(null)}
              aria-label="关闭"
            >
              ✕
            </button>
            <img 
              src={previewImageUrl} 
              alt="预览图片" 
              className="image-preview-img"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOpinionPage;

